<template>
  <form-input
    :name="name"
    :type="type"
    :number-type="numberType"
    :classes="classes"
    :placeholder="placeholder"
    :input-value="inputValue"
    :required="required"
    :max="max"
    :disabled="isDisabled"
    :is-update-in-process="planetStore.isUpdateInProcess"
    :ignore-update-in-process="ignoreUpdateInProcess"
    :is-invalid="isInvalid"
    :suffix="suffix"
    :error-message-text="errorMessageText"
    :tooltip="tooltip"
    :data-qa="dataQa"
    :readonly="readonly"
    :id="id"
    :icon="icon"
    :icon-location="iconLocation"
    :icon-clickable="iconClickable"
    :icon-props="iconProps"
    :label="label"
    ref="formInputRef"
    v-bind="$attrs"
    @focus="emit('focus', $event)"
    @blur="emit('blur', $event)"
    @keydown="onKeyDown($event)"
    @update:input-value="(value) => emit('update:inputValue', value)"
    @change:input-value="(value) => emit('change:inputValue', value)"
    @iconClicked="emit('iconClicked')"
  >
    <template #postLabel v-if="$slots.postLabel">
      <slot name="postLabel"></slot>
    </template>
  </form-input>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { GenericObject } from 'ui-component-library/rbz/types';
import FormInput from 'ui-component-library/rbz/components/FormInput.vue';
import { usePlanetStore } from '@/store/PlanetStore';

export type InputType = 'text' | 'password' | 'email' | 'number' | 'color';
export type NumberType = 'whole' | 'decimal';

interface RbzFormInputProps {
  name: string;
  label?: string;
  type?: InputType;
  numberType?: NumberType;
  classes?: string;
  placeholder?: string;
  inputValue: string | number;
  required?: boolean;
  max?: number;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  suffix?: string;
  isInvalid?: boolean;
  errorMessageText?: string;
  tooltip?: string;
  dataQa?: string;
  readonly?: boolean;
  id?: string;
  icon?: string;
  iconLocation?: 'left' | 'right';
  iconClickable?: boolean;
  iconProps?: GenericObject;
}

const props = withDefaults(defineProps<RbzFormInputProps>(), {
  label: '',
  type: 'text',
  numberType: 'whole',
  placeholder: '',
  classes: '',
  tooltip: '',
  dataQa: '',
  required: false,
  disabled: false,
  suffix: '',
  max: null,
  errorMessageText: '',
  id: '',
  icon: null,
  iconLocation: 'left',
  iconClickable: false,
  iconProps: null,
});

interface RbzFormInputEmits {
  (e: 'update:inputValue', value: string | number): void;

  (e: 'change:inputValue', value: string | number): void;

  (e: 'iconClicked'): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const planetStore = usePlanetStore();
const emit = defineEmits<RbzFormInputEmits>();

const isDisabled = computed(() => {
  return props.disabled || (planetStore.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const onKeyDown = (event: KeyboardEvent) => {
  if (props.type === 'number') {
    if (props.numberType === 'whole') {
      if (/^(\.|e|E)$/.test(event.key)) {
        event.preventDefault();
      }
    }
  }
};

const formInputRef = ref<FormInput | null>(null);

defineExpose({
  focus: () => formInputRef.value?.focus(),
  setIsDirty: (value: boolean) => formInputRef.value?.setIsDirty(value),
});
</script>
